import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import * as Styled from "./search.styled";
import { ExitIcon, PlusIcon, RemoveIcon, SearchIcon } from "./search-icons";
import {
  SearchFieldMetadata,
  SearchTabMetadata,
} from "../../../features/search/api/search-metadata.types";
import { useSearchMetadata } from "../../../features/search/api/use-search-metadata";
import { useSearchQuery } from "../../../features/search/api/use-search-query";
import { buildFieldValidator } from "../model/field-validation";
import { buildTableColumns } from "../model/column-renderers";
import { useSearchRowAction } from "../model/row-actions";
import { SearchResultsTable } from "../../../layout/results-table/ui/search-results-table";
import { ROUTES } from "../../../system/router/routes";

const chunkIntoRows = (fields: SearchFieldMetadata[], size: number) => {
  const rows: SearchFieldMetadata[][] = [];
  for (let i = 0; i < fields.length; i += size) {
    rows.push(fields.slice(i, i + size));
  }
  return rows;
};

interface SearchTabFormProps {
  tab: SearchTabMetadata;
}

const SearchTabForm = ({ tab }: SearchTabFormProps) => {
  const [visibleAdditional, setVisibleAdditional] = useState<string[]>([]);
  const [autoFocusField, setAutoFocusField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<Record<string, string>>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { results, isSearching, error, search } = useSearchQuery();
  const onRowSelect = useSearchRowAction(tab.results.rowAction);

  const validators = useMemo(
    () =>
      Object.fromEntries(
        tab.fields.map((field) => [field.name, buildFieldValidator(field)]),
      ),
    [tab],
  );

  const tableColumns = useMemo(
    () => buildTableColumns(tab.results.columns),
    [tab],
  );

  const visibleFields = tab.fields.filter(
    (field) => field.isDefault || visibleAdditional.includes(field.name),
  );
  const hiddenChips = tab.fields.filter(
    (field) => !field.isDefault && !visibleAdditional.includes(field.name),
  );

  // 3 or fewer fields: one line with the button to the right.
  // 4 or more: rows of max 3 equal-width fields, button below.
  const isInlineButton = visibleFields.length <= 3;
  const fieldRows = chunkIntoRows(visibleFields, 3);

  const addField = (name: string) => {
    setVisibleAdditional((prev) => [...prev, name]);
    setAutoFocusField(name);
  };

  const removeField = (name: string) => {
    setVisibleAdditional((prev) => prev.filter((field) => field !== name));
    setValue(name, "");
    clearErrors(name);
    if (autoFocusField === name) setAutoFocusField(null);
  };

  const onValid = async (data: Record<string, string>) => {
    const hasValue = visibleFields.some((field) =>
      (data[field.name] ?? "").trim(),
    );
    if (!hasValue) {
      setError("root", {
        type: "manual",
        message: "Please fill at least one search field",
      });
      return;
    }

    const criteria = Object.fromEntries(
      visibleFields
        .map((field) => [field.name, (data[field.name] ?? "").trim()])
        .filter(([, value]) => value),
    );
    await search(tab.key, criteria);
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    clearErrors("root");
    handleSubmit(onValid)(event);
  };

  const renderField = (field: SearchFieldMetadata) => {
    const fieldError = errors[field.name];
    const isRemovable = !field.isDefault;
    const inputId = `search-${tab.key}-${field.name}`;
    const registration = register(field.name, {
      validate: validators[field.name],
    });

    return (
      <Styled.FieldCell key={field.name}>
        <Styled.FieldLabel htmlFor={inputId}>{field.label}</Styled.FieldLabel>
        <Styled.InputWrap>
          {field.mask ? (
            <Styled.MaskedInput
              id={inputId}
              mask={field.mask}
              slotChar={field.slotChar}
              placeholder={field.placeholder}
              autoFocus={autoFocusField === field.name}
              $hasError={!!fieldError}
              $hasRemove={isRemovable}
              {...registration}
            />
          ) : (
            <Styled.Input
              id={inputId}
              type="text"
              placeholder={field.placeholder}
              autoFocus={autoFocusField === field.name}
              $hasError={!!fieldError}
              $hasRemove={isRemovable}
              {...registration}
            />
          )}
          {isRemovable && (
            <Styled.RemoveFieldBtn
              type="button"
              aria-label={`Remove ${field.label}`}
              onClick={() => removeField(field.name)}
            >
              <RemoveIcon />
            </Styled.RemoveFieldBtn>
          )}
          {fieldError && <Styled.Error>{fieldError.message}</Styled.Error>}
        </Styled.InputWrap>
      </Styled.FieldCell>
    );
  };

  const submitButton = (
    <Styled.SubmitBtn type="submit">
      <SearchIcon />
      Search
    </Styled.SubmitBtn>
  );

  return (
    <>
      <Styled.SearchControls $isResults={!!results || isSearching}>
        <Styled.Title>{tab.title}</Styled.Title>
        <SearchTabSelectorSlot />
        <Styled.SearchFormOuter>
          <Styled.SearchForm>
            <Styled.Form onSubmit={onSubmit}>
              <Styled.FormLayout>
                <Styled.FieldsGrid>
                  {fieldRows.map((row, index) => (
                    <Styled.FieldRow key={index}>
                      {row.map(renderField)}
                    </Styled.FieldRow>
                  ))}
                </Styled.FieldsGrid>
                {isInlineButton && (
                  <Styled.InlineBtnWrap>{submitButton}</Styled.InlineBtnWrap>
                )}
              </Styled.FormLayout>
              {hiddenChips.length > 0 && (
                <Styled.FieldControls>
                  {hiddenChips.map((field) => (
                    <Styled.ControlItem
                      key={field.name}
                      type="button"
                      onClick={() => addField(field.name)}
                    >
                      <PlusIcon />
                      {field.label}
                    </Styled.ControlItem>
                  ))}
                </Styled.FieldControls>
              )}
              {errors.root?.message && (
                <Styled.GlobalError>{errors.root.message}</Styled.GlobalError>
              )}
              {!isInlineButton && (
                <Styled.BelowBtnWrap>{submitButton}</Styled.BelowBtnWrap>
              )}
            </Styled.Form>
          </Styled.SearchForm>
        </Styled.SearchFormOuter>
      </Styled.SearchControls>

      {isSearching && (
        <Styled.LoadingWrap data-testid="search-loading">
          <Styled.Spinner />
          Loading results...
        </Styled.LoadingWrap>
      )}

      {!isSearching && error && (
        <Styled.NoResults role="alert">{error}</Styled.NoResults>
      )}

      {!isSearching && !error && results && (
        <>
          <Styled.ResultsHeader>
            <h1>Results ({results.total})</h1>
          </Styled.ResultsHeader>
          {results.total === 0 ? (
            <Styled.NoResults>
              No results found matching your search.
              <br />
              Please recheck the search request and try again.
            </Styled.NoResults>
          ) : (
            <SearchResultsTable
              records={results.rows}
              columns={tableColumns}
              onRowSelect={onRowSelect}
            />
          )}
        </>
      )}
    </>
  );
};

export const Search = () => {
  const navigate = useNavigate();
  const { metadata, isLoading, error, reload } = useSearchMetadata();
  const [selectedTabKey, setSelectedTabKey] = useState<string | null>(null);

  const activeTab =
    metadata?.tabs.find(
      (tab) => tab.key === (selectedTabKey ?? metadata.defaultTab),
    ) ?? metadata?.tabs[0];

  return (
    <Styled.Container>
      <Styled.TopRow>
        <Styled.Breadcrumbs>
          <Styled.BreadcrumbLink to={ROUTES.SEARCH}>
            Search
          </Styled.BreadcrumbLink>{" "}
          / LegacyLink
        </Styled.Breadcrumbs>
        <Styled.ExitAppBtn type="button" onClick={() => navigate(ROUTES.APP)}>
          <ExitIcon />
          Exit App
        </Styled.ExitAppBtn>
      </Styled.TopRow>

      {isLoading && (
        <Styled.LoadingWrap data-testid="search-metadata-loading">
          <Styled.Spinner />
          Loading search...
        </Styled.LoadingWrap>
      )}

      {!isLoading && error && (
        <Styled.NoResults role="alert">
          Failed to load the search page.
          <br />
          <Styled.RetryBtn type="button" onClick={reload}>
            Try again
          </Styled.RetryBtn>
        </Styled.NoResults>
      )}

      {!isLoading && !error && metadata && activeTab && (
        <SearchTabSelectorContext.Provider
          value={{
            tabs: metadata.tabs,
            activeKey: activeTab.key,
            onSelect: setSelectedTabKey,
          }}
        >
          {/* Remounting on tab change resets form, chips, and results */}
          <SearchTabForm key={activeTab.key} tab={activeTab} />
        </SearchTabSelectorContext.Provider>
      )}
    </Styled.Container>
  );
};

interface SearchTabSelectorContextValue {
  tabs: SearchTabMetadata[];
  activeKey: string;
  // eslint-disable-next-line no-unused-vars
  onSelect(key: string): void;
}

const SearchTabSelectorContext =
  React.createContext<SearchTabSelectorContextValue | null>(null);

const SearchTabSelectorSlot = () => {
  const context = React.useContext(SearchTabSelectorContext);
  if (!context) return null;

  return (
    <Styled.FormSelector role="tablist">
      {context.tabs.map((tab) => (
        <Styled.FormSelectorOption
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={tab.key === context.activeKey}
          $active={tab.key === context.activeKey}
          onClick={() => context.onSelect(tab.key)}
        >
          {tab.tabLabel}
        </Styled.FormSelectorOption>
      ))}
    </Styled.FormSelector>
  );
};
